import React, { useReducer, useState } from "react";
import PropTypes from "prop-types";
import SpecializedTransactionTable from "../../elements/Tables/SpecializedTransactionTable/SpecializedTransactionTable";
import { useFetch } from "../../../Utils/useFetch";
import {
  FETCH_ALL_DEPT_API,
  FETCH_ALL_DISCOUNT_API,
  FETCH_ALL_TRANSACTION_API,
} from "../../../Constants/Apis";
import { Button, Input, message, Modal, Select, DatePicker, Form } from "antd";
import { reducer, headers } from "../../../Utils/CommonUtils";
import axios from "axios";
import { deleteTransaction } from "../../../Services/TransactionService";
import * as XLSX from "xlsx";
import dayjs from "dayjs";

const { Search } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;

const TroubleTransactionPage = ({ history, userData }) => {
  const initialState = {
    searchKeyword: "",
    departmentFilter: userData?.counterCode === "ECOMMRI" ? "E-COMMERCE" : "",
    dateRange: {
      startDate: dayjs().subtract(7, "day").format("YYYY-MM-DD"),
      endDate: dayjs().format("YYYY-MM-DD"),
    },
    filteredInfo: {},
    sorter: {},
    current: 1,
    pageSize: 10,
  };

  const [state, dispatch] = useReducer(reducer, initialState);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [transactionId, setTransactionId] = useState(null);
  const [modalForm] = Form.useForm();

  // Prepare API parameters with proper counterCode filtering
  const apiParams = {
    searchKeyword: state.searchKeyword,
    departmentFilter: userData?.counterCode === "ECOMMRI" 
      ? "E-COMMERCE"  // ECOMMRI users can only see E-COMMERCE
      : (state.departmentFilter === "E-COMMERCE" ? "" : state.departmentFilter), // Non-ECOMMRI users cannot see E-COMMERCE
    dateRange: state.dateRange,
    filteredInfo: state.filteredInfo,
    sorter: state.sorter,
    current: state.current,
    pageSize: state.pageSize,
    // Add counterCode to ensure backend filtering works correctly
    counterCode: userData?.counterCode || "",
  };


  const {
    fetchedData: transactionResponse,
    callReFetch,
    loading,
  } = useFetch(`${FETCH_ALL_TRANSACTION_API}/trouble`, apiParams);

  const { fetchedData: { data: discountData = [] } = {} } = useFetch(
    `${FETCH_ALL_DISCOUNT_API}`
  );

  const { fetchedData: { data: deptData = [] } = {} } = useFetch(
    `${FETCH_ALL_DEPT_API}`
  );

  const data = transactionResponse?.data?.data || [];
  const paginationInfo = transactionResponse?.data?.pagination || {
    current: 1,
    pageSize: 10,
    total: 0,
  };

  const handleDelete = (id) => {
    setTransactionId(id);
    setIsModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteTransaction(transactionId);
      message.success("Transaction deleted successfully");
      callReFetch();
    } catch (error) {
      message.error(
        error?.response?.data?.message ||
          "Something went wrong, failed to delete transaction."
      );
    }
    setIsModalOpen(false);
  };

  const handleSearch = (value) => {
    dispatch({ type: "searchKeyword", payload: value });
    callReFetch();
  };

  const handleDeptFilterChange = (value) => {
    dispatch({ type: "departmentFilter", payload: value });
    callReFetch();
  };

  const handleDateChange = (dates) => {
    const [start, end] = dates || [];
    dispatch({
      type: "dateRange",
      payload: {
        startDate: start ? start.format("YYYY-MM-DD") : null,
        endDate: end ? end.format("YYYY-MM-DD") : null,
      },
    });
    callReFetch();
  };

  const handlePaginationChange = (page, pageSize) => {
    dispatch({ type: "current", payload: page });
    dispatch({ type: "pageSize", payload: pageSize });
    callReFetch();
  };

  const handleDownloadExcel = async () => {
    try {
      // Prepare API parameters for downloading all data without pagination
      const downloadParams = {
        searchKeyword: state.searchKeyword,
        departmentFilter: userData?.counterCode === "ECOMMRI" 
          ? "E-COMMERCE"  // ECOMMRI users can only see E-COMMERCE
          : (state.departmentFilter === "E-COMMERCE" ? "" : state.departmentFilter), // Non-ECOMMRI users cannot see E-COMMERCE
        dateRange: state.dateRange,
        filteredInfo: state.filteredInfo,
        sorter: state.sorter,
        current: 1,
        pageSize: 999999, // Large number to get all records
        // Add counterCode to ensure backend filtering works correctly
        counterCode: userData?.counterCode || "",
      };

      // Use the same fetch mechanism as the table data
      const response = await axios.get(`${FETCH_ALL_TRANSACTION_API}/trouble`, {
        params: downloadParams,
        ...headers(),
      });

      const allData = response?.data?.data || [];
      if (!allData || allData.length === 0) {
        message.warning("No transactions to download");
        return;
      }

      const headerRows = [
        {
          "Date Range": `${state.dateRange.startDate} to ${state.dateRange.endDate}`,
        },
        {},
        {
          "Transaction Date": "Transaction Date",
          "Counter Name": "Counter Name",
          Department: "Department",
          "No Bon": "No Bon",
          Barcode: "Barcode",
          Article: "Article",
          Color: "Color",
          Quantity: "Quantity",
          "Problematic Type": "Problematic Type",
        },
      ];

      const formattedData = allData.map((item) => ({
        "Transaction Date": item.transactionDate
          ? new Date(item.transactionDate).toLocaleDateString()
          : "N/A",
        "Counter Name": item.counterName || "",
        Department: item.counterDept || "",
        "No Bon": item.noBon || "",
        Barcode: item.barcode || "",
        Article: item.article || "",
        Color: item.color || "",
        Quantity: item.quantity || 0,
        "Problematic Type":
          item.bR === 1
            ? "Stock < 0"
            : item.bR === 2
            ? "Transaction w/o Stock"
            : "-",
      }));

      const excelData = [...headerRows, ...formattedData];
      const worksheet = XLSX.utils.json_to_sheet(excelData, { skipHeader: true });
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(
        workbook,
        worksheet,
        "Problematic Transactions"
      );
      XLSX.writeFile(workbook, "Problematic_Transactions.xlsx");
      
      message.success(`Downloaded ${allData.length} problematic transactions successfully`);
    } catch (error) {
      message.error("Failed to download Excel file");
      console.error("Download error:", error);
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center", // Center all children horizontally
        padding: "20px",
      }}
    >
      {" "}
      <Modal
        title="Confirm Delete"
        open={isModalOpen}
        onOk={handleConfirmDelete}
        onCancel={() => setIsModalOpen(false)}
        okText="Delete"
        cancelText="Cancel"
      >
        Are you sure you want to delete this transaction?
      </Modal>
      {/* Filters and Download Excel */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "10px",
          alignItems: "center",
          marginBottom: "20px",
        }}
      >
        <Search
          placeholder="Search by Barcode or No Bon"
          onSearch={handleSearch}
          style={{ width: 250 }}
        />
        <Select
          placeholder="Filter by Department"
          style={{ width: 200 }}
          onChange={handleDeptFilterChange}
          value={state.departmentFilter}
          disabled={userData?.counterCode === "ECOMMRI"}
        >
          {deptData
            .filter((dept) => {
              // If user is ECOMMRI, only show E-COMMERCE
              if (userData?.counterCode === "ECOMMRI") {
                return dept.name === "E-COMMERCE";
              }
              // If user is not ECOMMRI, exclude E-COMMERCE
              return dept.name !== "E-COMMERCE";
            })
            .map((dept) => (
              <Option key={dept._id} value={dept.name}>
                {dept.name}
              </Option>
            ))}
        </Select>
        <RangePicker
          value={[
            state.dateRange.startDate ? dayjs(state.dateRange.startDate) : null,
            state.dateRange.endDate ? dayjs(state.dateRange.endDate) : null,
          ]}
          onChange={handleDateChange}
          format="YYYY-MM-DD"
        />
        <Button type="primary" onClick={handleDownloadExcel}>
          Download Excel
        </Button>
      </div>
      <SpecializedTransactionTable
        data={data}
        loading={loading}
        pagination={{
          current: paginationInfo.current,
          pageSize: paginationInfo.pageSize,
          total: paginationInfo.total,
          onChange: handlePaginationChange,
        }}
        handleDelete={handleDelete}
        discountData={discountData}
        dispatch={dispatch}
        callReFetch={callReFetch}
        userData={userData}
      />
    </div>
  );
};

TroubleTransactionPage.propTypes = {
  history: PropTypes.object.isRequired,
  userData: PropTypes.object,
};

export default TroubleTransactionPage;
